/**
 * 依頼ごとの冪等キー（UUID v4）を作る。応答を受け取れずに再送しても、依頼を重複して作らないために送る。
 * crypto.randomUUID が使えない環境では、getRandomValues から作る。
 * @returns UUID v4 の文字列
 */
export const generateIdempotencyKey = (): string => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  // バージョン4・バリアント10 のビットを立てる
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
};

const STORAGE_PREFIX = "reassistFetchPendingKey:";

/**
 * 依頼内容に対する冪等キーを返す。応答を受け取れずに画面を閉じ、同じ内容で再依頼しても、同じキーを使う
 * （サーバーが重複を作らず最初の依頼IDを返す）。キーはタブのセッション中だけ保持する。
 * @param fingerprint - 依頼内容の同一判定用の文字列（`buildRequestFingerprint` の戻り値）
 * @returns 保持中のキー。無ければ新しく作って保持する
 */
export const getPendingIdempotencyKey = (fingerprint: string): string => {
  const storageKey = `${STORAGE_PREFIX}${fingerprint}`;
  try {
    const saved = window.sessionStorage.getItem(storageKey);
    if (saved) return saved;
    const created = generateIdempotencyKey();
    window.sessionStorage.setItem(storageKey, created);
    return created;
  } catch {
    // ストレージが使えない環境では、保持せずに新しいキーを返す
    return generateIdempotencyKey();
  }
};

/**
 * 依頼が受け付けられたら、その内容のキーを破棄する（次に同じ内容で依頼する場合は、別の依頼として扱う）。
 * @param fingerprint - 依頼内容を表す文字列
 */
export const clearPendingIdempotencyKey = (fingerprint: string): void => {
  try {
    window.sessionStorage.removeItem(`${STORAGE_PREFIX}${fingerprint}`);
  } catch {
    // ストレージが使えない環境では何もしない
  }
};

/**
 * 保持中の冪等キーをすべて破棄する。ログアウト時に呼び、同じタブで入り直した別ユーザーが前のユーザーのキーを使わないようにする。
 */
export const clearAllPendingIdempotencyKeys = (): void => {
  try {
    const storage = window.sessionStorage;
    for (let i = storage.length - 1; i >= 0; i -= 1) {
      const key = storage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) storage.removeItem(key);
    }
  } catch {
    // ストレージが使えない環境では何もしない
  }
};

/** バイト列の SHA-256 を16進文字列で返す。 */
const sha256Hex = async (data: BufferSource): Promise<string> => {
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest), (b) =>
    b.toString(16).padStart(2, "0")
  ).join("");
};

/** 添付ファイル1件の同一判定用の値。名前・MIME type・内容の SHA-256 を使い、ハッシュが使えない環境では名前・サイズ・更新日時で代える。 */
const fingerprintFile = async (file: File): Promise<string> => {
  try {
    const content = await sha256Hex(await file.arrayBuffer());
    return `${file.name}:${file.type}:${content}`;
  } catch {
    return `${file.name}:${file.type}:${file.size}:${file.lastModified}`;
  }
};

/**
 * 依頼の送信内容全体から、同一判定用の文字列（SHA-256）を作る。添付ファイルは名前・MIME type・内容のハッシュで区別する。
 * 送信する項目が1つでも違えば違う文字列になる（別の内容の依頼に同じキーを使わないため）。
 * @param payload - 送信する依頼の内容（冪等キーを除く）
 * @returns 同一判定用の文字列
 */
export const buildRequestFingerprint = async (
  payload: Record<string, unknown>
): Promise<string> => {
  const files = Array.isArray(payload.relatedFiles)
    ? (payload.relatedFiles as File[])
    : [];
  const fileFingerprints = await Promise.all(files.map(fingerprintFile));
  const serialized = JSON.stringify({
    ...payload,
    relatedFiles: undefined,
    relatedFileFingerprints: fileFingerprints,
  });
  try {
    return await sha256Hex(new TextEncoder().encode(serialized));
  } catch {
    // ハッシュが使えない環境では、内容そのものを同一判定に使う
    return serialized;
  }
};
